import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Loader2, 
  Mail, 
  RefreshCw,
  ArrowLeft,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VerifyOTP = () => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const previousThemeRef = useRef(theme);
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [email, setEmail] = useState('');
  const [pendingToken, setPendingToken] = useState('');
  const [verified, setVerified] = useState(false);
  
  const inputRefs = useRef([]);

  // Force dark mode
  useEffect(() => {
    previousThemeRef.current = theme;
    if (theme !== 'dark') {
      setTheme('dark');
    }
    return () => {
      if (previousThemeRef.current !== 'dark') {
        setTheme(previousThemeRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load pending signup info
  useEffect(() => {
    const token = sessionStorage.getItem('pending_signup_token');
    const storedEmail = sessionStorage.getItem('pending_signup_email');
    
    if (!token || !storedEmail) {
      toast.error('No pending signup found');
      navigate('/free-trial');
      return;
    }
    
    setPendingToken(token);
    setEmail(storedEmail);
  }, [navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Handle OTP input
  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when complete
    if (value && index === 5 && newOtp.every(d => d)) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);
      handleVerify(pastedData);
    }
  };

  const handleVerify = async (otpCode) => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/trial/verify-otp`, {
        pending_token: pendingToken,
        otp_code: otpCode
      });
      
      setVerified(true);
      
      // Clear session storage
      sessionStorage.removeItem('pending_signup_token');
      sessionStorage.removeItem('pending_signup_email');
      
      // Store the onboarding flag in localStorage (persists across page loads)
      localStorage.setItem('show_onboarding_wizard', 'true');
      localStorage.setItem('new_tenant_client_id', response.data.client_id);
      
      toast.success('Email verified!', {
        description: 'Redirecting to setup...'
      });
      
      // Auto-login using the token
      setTimeout(async () => {
        try {
          const loginResponse = await axios.post(`${API_URL}/trial/auto-login`, null, {
            params: { token: response.data.auto_login_token }
          });
          
          // Store auth data and redirect
          localStorage.setItem('token', loginResponse.data.access_token);
          localStorage.setItem('user', JSON.stringify(loginResponse.data.user));
          
          // Reload to trigger auth context
          window.location.href = '/onboarding';
        } catch (loginError) {
          // If auto-login fails, show manual login info
          toast.info('Please log in with your credentials', {
            description: `Client ID: ${response.data.client_id}, Username: ${response.data.username}`
          });
          navigate('/login');
        }
      }, 1500);
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid verification code';
      toast.error(message);
      // Clear OTP on error
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setResending(true);
    try {
      const response = await axios.post(`${API_URL}/trial/resend-otp`, {
        pending_token: pendingToken
      });
      
      // Update token if a new one is returned
      if (response.data.pending_token) {
        setPendingToken(response.data.pending_token);
        sessionStorage.setItem('pending_signup_token', response.data.pending_token);
      }
      
      toast.success('New code sent!', {
        description: 'Please check your email.'
      });
      
      // Start 60 second cooldown
      setResendCooldown(60);
      
      // Clear current OTP
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to resend code';
      toast.error(message);
    } finally {
      setResending(false);
    }
  };

  const maskedEmail = email ? 
    email.replace(/(.{2})(.*)(@.*)/, '$1***$3') : '';

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center p-4">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#ffd108]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#ffd108]/3 rounded-full blur-2xl" />
      </div>

      <Card className="w-full max-w-md relative z-10 border-zinc-800 bg-zinc-900/80 backdrop-blur-sm">
        <CardHeader className="text-center pb-2">
          <div className="flex justify-center mb-4">
            <div className={`p-4 rounded-full ${verified ? 'bg-green-500/20' : 'bg-[#ffd108]/20'}`}>
              {verified ? (
                <CheckCircle2 className="h-10 w-10 text-green-500" />
              ) : (
                <Mail className="h-10 w-10 text-[#ffd108]" />
              )}
            </div>
          </div>
          <CardTitle className="font-heading text-2xl text-white">
            {verified ? 'Email Verified!' : 'Verify Your Email'}
          </CardTitle>
          <CardDescription className="text-zinc-400 mt-2">
            {verified ? (
              'Your account has been created. Setting up your workspace...'
            ) : (
              <>
                We've sent a 6-digit code to<br />
                <span className="text-white font-medium">{maskedEmail}</span>
              </>
            )}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-6">
          {!verified && (
            <>
              {/* OTP Input */}
              <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <Input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    disabled={loading}
                    data-testid={`otp-input-${index}`}
                    className="w-12 h-14 text-center text-2xl font-mono bg-zinc-800 border-zinc-700 focus:border-[#ffd108] text-white"
                  />
                ))}
              </div>

              {/* Verify Button */}
              <Button
                onClick={() => handleVerify(otp.join(''))}
                disabled={loading || otp.some(d => !d)}
                className="w-full btn-volt py-5"
                data-testid="verify-otp-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </Button>

              {/* Resend Section */}
              <div className="mt-6 text-center">
                <p className="text-zinc-500 text-sm mb-2">
                  Didn't receive the code?
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResendOTP}
                  disabled={resending || resendCooldown > 0}
                  className="text-[#ffd108] hover:text-[#ffd108] hover:bg-[#ffd108]/10"
                  data-testid="resend-otp-btn"
                >
                  {resending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : resendCooldown > 0 ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Resend in {resendCooldown}s
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Resend Code
                    </>
                  )}
                </Button>
              </div>

              {/* Back Link */}
              <div className="mt-6 pt-6 border-t border-zinc-800 text-center">
                <button
                  onClick={() => navigate('/free-trial')}
                  className="text-zinc-500 hover:text-zinc-300 text-sm inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to signup
                </button>
              </div>
            </>
          )}

          {verified && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyOTP;
